/**
 * Labstep
 *
 * @module grid/Index/coldefs/amount
 * @desc Column Definition for amount
 */

import type { ValueGetterParams } from 'ag-grid-community';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import type { ColDef } from 'labstep-web/core/DataGrid/types';
import {
  getEditable,
  getEntityDefault,
} from 'labstep-web/core/DataGrid/utils';
import { ProtocolCondition } from 'labstep-web/models/protocol-condition.model';
import { ProtocolValue } from 'labstep-web/models/protocol-value.model';
import { ResourceItem } from 'labstep-web/models/resource-item.model';
import type React from 'react';
import type { colDefAmountType } from './types';
import { getCanCreateProtocolValue, getValueSetter } from './utils';

/**
 * Column definition for Protocol Value amount
 * @param options Options
 * @returns Column definition
 */
const colDefAmount: colDefAmountType = (options = {}) => {
  const {
    getNestedEntity,
    columnProps,
    updateUnit,
    variableTemplate,
    experimentId,
    protocolId,
  } = options;
  const editable = getEditable(getNestedEntity, () => [
    'amount',
    'unit',
  ]);
  const getEntity = getNestedEntity || getEntityDefault;
  const cellRenderer: ColDef<
    ProtocolValue | ResourceItem
  >['cellRenderer'] = (
    params,
  ): string | React.ReactElement | null => {
    const fallback = (
      <DataGridPlaceholder
        params={params}
        editable={editable}
        placeholder="No amount"
      />
    );
    const entity = getEntity(params);
    return entity instanceof ProtocolValue ||
      entity instanceof ResourceItem
      ? entity.amountUnitTextNoUnitOnly || fallback
      : fallback;
  };

  return {
    cellRenderer,
    editable: (params) => {
      const entity = params.data;
      if (entity instanceof ProtocolCondition) {
        return getCanCreateProtocolValue(experimentId, protocolId);
      }
      return editable(params);
    },
    valueGetter: (
      params: ValueGetterParams,
    ): string | boolean | null => {
      const entity = getEntity(params);
      // we must return false instead of null
      // otherwise the grid thinks the value has not updated
      // when the entity is set and its value is null
      return entity ? entity.amountUnitTextNoUnitOnly : null;
    },
    valueGetterExport: (params: ValueGetterParams) =>
      params.node?.data?.amountUnitTextNoUnitOnly || '',
    valueSetter: getValueSetter(
      getEntity,
      updateUnit,
      variableTemplate,
      experimentId,
      protocolId,
    ),
    ...columnProps,
  };
};

export default colDefAmount;
